import React, { useContext, useEffect } from 'react'
import { ApolloError, gql, useQuery } from '@apollo/client'
import { DateTime } from 'luxon'
import { getProjectMetaQuery, ProjectMeta } from '@/services/ProjectsService'
import { WatchPartyChatContextProvider } from '@/services/WatchPartyChatService/WatchPartyChatService'

export enum LivestreamMode {
  LOADING,
  ERROR,
  PLAY,
  STREAM_OVER,
  COUNT_DOWN,
  UNAVAILABLE_GEOGATED,
  GUILD_EXCLUSIVE,
}

interface LivestreamStateContextValues {
  livestream?: {
    id: string
    guid: string
    name: string
    url: string
    projectSlug: string
    bannerCloudinaryPath: string
    description: string
    isEnded?: boolean
    live?: boolean
    startAt?: string
    chatCode: string
    actionImageUrl?: string
    actionMessage?: string
    actionTargetUrl?: string
    unavailableReason?: string
  }
  error?: ApolloError
  mode: LivestreamMode
  projectMeta?: ProjectMeta
}

const defaultValues = {
  livestream: undefined,
  isLoading: true,
  error: undefined,
  isCalled: false,
  mode: LivestreamMode.LOADING,
  projectMeta: undefined,
} as LivestreamStateContextValues

const LivestreamStateContext = React.createContext(defaultValues)

export const useLivestream = (): LivestreamStateContextValues => {
  return useContext(LivestreamStateContext)
}

interface LivestreamProviderProps {
  children: React.ReactNode
  streamId: string
  projectSlug: string
}

const GET_STREAM = gql`
  query getStream($guid: String!) {
    livestreamByGuid(guid: $guid) {
      id
      guid
      name
      url(input: { segmentFormat: TS })
      bannerCloudinaryPath
      isEnded
      live
      startAt
      description
      projectSlug
      chatCode: partyCode
      actionImageUrl
      actionMessage
      actionTargetUrl
      unavailableReason
    }
  }
`

const ILLUMINATE_2023_GUID = 'cc5a136c-377b-4d18-b9a5-122d024f66f5'

export const LivestreamProvider: React.FC<LivestreamProviderProps> = ({ children, streamId, projectSlug }) => {
  const { data, loading, error, called, refetch } = useQuery(GET_STREAM, {
    nextFetchPolicy: 'network-only',
    variables: { guid: streamId },
    errorPolicy: 'all',
  })

  const { data: projectData } = useQuery(getProjectMetaQuery, {
    variables: { slug: projectSlug },
    errorPolicy: 'all',
  })

  useEffect(() => {
    if (data?.livestreamByGuid?.unavailableReason || data?.livestreamByGuid?.isEnded) return

    const interval = setInterval(() => {
      refetch()
    }, 10000)

    return () => {
      clearInterval(interval)
    }
  }, [refetch, data?.livestreamByGuid?.unavailableReason, data?.livestreamByGuid?.isEnded])
  let mode
  const livestream = data?.livestreamByGuid

  const millisToStart = DateTime.fromISO(livestream?.startAt).diffNow('milliseconds')

  if (livestream?.unavailableReason === 'GEOBLOCKED') {
    mode = LivestreamMode.UNAVAILABLE_GEOGATED
  } else if (livestream?.unavailableReason === 'PRERELEASE') {
    mode = LivestreamMode.GUILD_EXCLUSIVE
  } else if (loading) {
    mode = LivestreamMode.LOADING
  } else if (error || (called && !livestream)) {
    mode = LivestreamMode.ERROR
  } else if (livestream?.url) {
    mode = LivestreamMode.PLAY
  } else if (livestream?.isEnded) {
    mode = LivestreamMode.STREAM_OVER
  } else if (millisToStart.toMillis() > 0) {
    mode = LivestreamMode.COUNT_DOWN
  } else {
    mode = LivestreamMode.ERROR
  }

  return (
    <LivestreamStateContext.Provider
      value={{
        mode,
        livestream,
        projectMeta: projectData?.project,
        error,
      }}
    >
      <WatchPartyChatContextProvider
        context={livestream?.guid === ILLUMINATE_2023_GUID ? 'illuminate-2023' : undefined}
        chatCode={livestream?.chatCode}
        projectSlug={projectSlug}
      >
        {children}
      </WatchPartyChatContextProvider>
    </LivestreamStateContext.Provider>
  )
}
